import {
  ArrowArcLeft,
  ArrowArcRight,
  BracketsCurly,
  MagicWand,
  Smiley,
} from "@phosphor-icons/react";

import { extractAiDescriptors } from "features/templates/utils";

import EmojiSelector from "common/components/EmojiSelector";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import Variables from "./Variables";

import type { Content, Editor } from "@tiptap/react";
import type { DetailedCampaign } from "../../../../../pages/campaigns/types";

import MagicWandIcon from "assets/icons/magic-wand.svg?react";

interface ToolbarProps {
  lastFocusedField: { type: "message" | "subject"; editor: Editor };
  customVariables: DetailedCampaign["custom_variable_keys"];
  uploadAttachmentButtonComponent: JSX.Element | undefined;
}

export default function Toolbar({
  lastFocusedField,
  customVariables,
  uploadAttachmentButtonComponent,
}: ToolbarProps) {
  const isDesktop = useTwBreakpoint("lg");

  const focusedEditor = lastFocusedField.editor;

  function insertContent(content: Content) {
    focusedEditor.chain().focus().insertContent(content).run();
  }

  function addAiVariable() {
    const aiDescriptors = extractAiDescriptors(focusedEditor.getJSON().content);
    const existingIds = Object.keys(aiDescriptors);

    let newId: string;
    for (let i = 1; ; i += 1) {
      newId = `VAR_${i}`;
      if (!existingIds.includes(newId)) break;
    }

    insertContent({
      type: "aiVariable",
      attrs: {
        id: newId,
        description: "",
      },
    });
  }

  const buttonSize = isDesktop ? "xs" : "md";

  return (
    <div className="flex items-center p-1 lg:p-2">
      <div className="flex items-center lg:gap-x-2">
        <RenderIf condition={lastFocusedField.type === "message"}>
          {uploadAttachmentButtonComponent}
        </RenderIf>

        <EmojiSelector
          onEmojiSelection={(selectedEmoji) => {
            insertContent(selectedEmoji);
          }}
          buttonProps={{
            size: buttonSize,
            children: <Smiley />,
          }}
        />

        <Variables customVariables={customVariables} editor={focusedEditor} />

        {/* Spintax */}
        <Button
          onClick={() =>
            insertContent("{SPIN}Option 1|Option 2|Option 3{ENDSPIN}")
          }
          disabled={!focusedEditor.isEditable}
          size={buttonSize}
          variant="ghost-black"
          intent="iconOnly"
          aria-label="Spintax"
        >
          <BracketsCurly />
        </Button>

        {/* AI Variable */}
        <RenderIf condition={lastFocusedField.type === "message"}>
          <Button
            onClick={addAiVariable}
            disabled={!focusedEditor.isEditable}
            size={buttonSize}
            variant="ghost-black"
            intent="iconOnly"
            aria-label="Spintax"
          >
            {focusedEditor.isEditable ? <MagicWandIcon /> : <MagicWand />}
          </Button>
        </RenderIf>
      </div>

      {/* Undo / Redo */}
      <div className="ml-auto flex items-center gap-x-1 lg:gap-x-2">
        <Button
          onClick={() => focusedEditor.chain().focus().undo().run()}
          disabled={!focusedEditor.can().undo() || !focusedEditor.isEditable}
          size={buttonSize}
          variant="secondary-black"
          intent="iconOnly"
          aria-label="Undo"
        >
          <ArrowArcLeft />
        </Button>

        <Button
          onClick={() => focusedEditor.chain().focus().redo().run()}
          disabled={!focusedEditor.can().redo() || !focusedEditor.isEditable}
          size={buttonSize}
          variant="secondary-black"
          intent="iconOnly"
          aria-label="Redo"
        >
          <ArrowArcRight />
        </Button>
      </div>
    </div>
  );
}
